/**
 * transcriptionQueueStore — tracks background voice-input transcriptions and
 * their stages so the status-footer badge can surface live progress in a
 * popover. Each job moves transcribing → (enhancing) → done | error, carrying
 * per-stage timing and, on failure, a copyable error message. Finished/errored
 * jobs linger briefly so the user can still see recent results after they
 * complete, then auto-clear (errors linger longer so the message can be grabbed).
 */
import { create } from 'zustand';

export type TranscriptionStage = 'transcribing' | 'enhancing' | 'done' | 'error';

export interface TranscriptionJob {
  id: number;
  /** Project name captured at recording-start, for display. */
  projectName: string;
  /** Whether this job runs an AI-enhance stage after transcription. */
  enhanced: boolean;
  stage: TranscriptionStage;
  /** ms epoch when transcription started. */
  startedAt: number;
  /** ms epoch when the enhance stage began (enhanced jobs only). */
  enhanceStartedAt?: number;
  /** ms epoch when the job reached done/error. */
  completedAt?: number;
  /** Error message (stage === 'error'); rendered as a copyable row. */
  error?: string;
  /** Short preview of the final text (stage === 'done'). */
  preview?: string;
}

/** How long a finished/errored job lingers before auto-clearing. Errors stay
 *  longer so the user has time to open the popover and copy the message. */
const DONE_LINGER_MS = 3 * 60 * 1000;
const ERROR_LINGER_MS = 15 * 60 * 1000;

interface TranscriptionQueueState {
  jobs: Map<number, TranscriptionJob>;
  nextId: number;

  /** Begin a job in the 'transcribing' stage. Returns its id. */
  start: (projectName: string, enhanced: boolean) => number;
  /** Move a job into the 'enhancing' stage. */
  beginEnhance: (id: number) => void;
  /** Mark a job done; it lingers briefly so the result stays visible. */
  finish: (id: number, preview?: string) => void;
  /** Mark a job errored; lingers longer and carries a copyable message. */
  fail: (id: number, error: string) => void;
  /** Remove a job now (manual dismiss / auto-clear). */
  dismiss: (id: number) => void;
  /** Safety net: if a job is still mid-flight (early return, no result), drop it. */
  settle: (id: number) => void;
  /** Remove all finished/errored jobs (the popover's "clear" action). */
  clearFinished: () => void;
}

export const useTranscriptionQueueStore = create<TranscriptionQueueState>((set, get) => {
  const mutate = (id: number, fn: (j: TranscriptionJob) => TranscriptionJob) =>
    set((state) => {
      const cur = state.jobs.get(id);
      if (!cur) return state;
      const next = new Map(state.jobs);
      next.set(id, fn(cur));
      return { jobs: next };
    });

  return {
    jobs: new Map(),
    nextId: 1,

    start: (projectName, enhanced) => {
      const id = get().nextId;
      set((state) => {
        const next = new Map(state.jobs);
        next.set(id, { id, projectName, enhanced, stage: 'transcribing', startedAt: Date.now() });
        return { jobs: next, nextId: state.nextId + 1 };
      });
      return id;
    },

    beginEnhance: (id) =>
      mutate(id, (j) => ({ ...j, stage: 'enhancing', enhanceStartedAt: Date.now() })),

    finish: (id, preview) => {
      mutate(id, (j) => ({ ...j, stage: 'done', completedAt: Date.now(), preview }));
      setTimeout(() => get().dismiss(id), DONE_LINGER_MS);
    },

    fail: (id, error) => {
      mutate(id, (j) => ({ ...j, stage: 'error', completedAt: Date.now(), error }));
      setTimeout(() => get().dismiss(id), ERROR_LINGER_MS);
    },

    dismiss: (id) =>
      set((state) => {
        if (!state.jobs.has(id)) return state;
        const next = new Map(state.jobs);
        next.delete(id);
        return { jobs: next };
      }),

    settle: (id) => {
      const j = get().jobs.get(id);
      if (j && (j.stage === 'transcribing' || j.stage === 'enhancing')) get().dismiss(id);
    },

    clearFinished: () =>
      set((state) => {
        const next = new Map(state.jobs);
        for (const [id, j] of next) if (j.stage === 'done' || j.stage === 'error') next.delete(id);
        return { jobs: next };
      }),
  };
});
